// Single source of truth for request-state display text, shared by
// VaultEstateDashboard, VaultRequestCards, EntityVaultRequestOverviewCard,
// and EntityVaultRequestTrailCard — previously each component hand-rolled
// its own copy of this map and they'd drifted (e.g. `stuck` rendered as
// "Stalled" in two places, "STUCK" and "Stuck" in the other two). Colors/
// styling stay local to each component — those are legitimately different
// per presentation context (chip vs. card vs. timeline).
export const STATE_LABEL: Record<string, string> = {
  submitted: 'Submitted',
  pending_approval: 'Pending Approval',
  rejected_approval: 'Rejected',
  standard_redirect: 'Standard Change — Redirected',
  approved: 'Approved',
  pr_open: 'PR Open',
  scheduled: 'Scheduled',
  deploying: 'Deploying',
  deployed: 'Deployed',
  completed: 'Completed',
  deploy_failed: 'Deploy Failed',
  // Working name pending ICE confirmation (DEPLOY-INT,
  // docs/mvp/07-open-questions.md) — the code constant deploy_interrupted
  // stays regardless of any future label wording change.
  deploy_interrupted: 'Deploy Interrupted',
  cleanup_failed: 'Cleanup Failed',
  stuck: 'Stalled',
  abandoned: 'Canceled',
  pr_dropped: 'PR Dropped',
  gate2_denied: 'Gate 2 Denied',
  decommission_pending: 'Decommission Pending',
  // Edit lifecycle (ClickUp 86e21uzu5) — set on the original request once
  // its linked edit reaches 'deployed'. Terminal.
  superseded: 'Superseded',
  // snow_tech_lead_approved/snow_group_approved/snow_platform_approved/
  // snow_manager_approved/snow_ar_pending/snow_exception_review
  // deliberately absent — per docs/mvp/07-open-questions.md and GH #598,
  // ServiceNow only ever pushes the terminal approved/rejected decision;
  // these intermediate milestones can never actually be driven by a real
  // SNOW signal and are dead code slated for removal. The only real SNOW
  // states are pending_approval, approved, rejected_approval, stuck, and
  // snow_stalled. Falling through to the UPPERCASE fallback here is
  // deliberate — it should never render, and if it ever does, the ugly
  // fallback is a signal something's wrong, not a state worth polishing.
  snow_stalled: 'Stalled in ServiceNow',
  gitops_stalled: 'Stalled in GitOps',
};

export function stateLabel(state: string): string {
  return STATE_LABEL[state] ?? state.toUpperCase().replace(/_/g, ' ');
}

const STALL_REASON_TEXT: Record<string, string> = {
  unavailable: 'temporarily unavailable — retrying automatically',
  rejected: 'rejected — needs review',
  job_failed: 'job failed — needs review',
};

const STALL_SERVICE_TEXT: Record<string, string> = {
  servicenow: 'ServiceNow',
  gitops: 'GitOps',
  opa: 'OPA',
  awx: 'AWX',
};

// Hover-only tooltip text for a stalled status chip — the visible chip
// label never changes, this is additional context shown on hover only.
// Returns null when there's nothing stall-specific to say (normal states,
// or a stall-eligible state with no stall data attached).
export function adminActionTooltip(
  stallReason?: string | null,
  stallService?: string | null,
): string | null {
  if (!stallReason) return null;
  const reason = STALL_REASON_TEXT[stallReason] ?? stallReason;
  const service = stallService ? STALL_SERVICE_TEXT[stallService] : undefined;
  return `Admin Action Required — ${service ? `${service} ` : ''}${reason}`;
}

// A request's latest stall event belongs to exactly one pipeline stage —
// ServiceNow approval, or GitOps/AWX deployment. The dashboard renders a
// separate chip per stage, and only the chip for the stage that actually
// stalled should carry the tooltip: a GitOps stall on deployment doesn't
// mean the (already-succeeded) SNOW approval needs admin attention too.
const APPROVAL_STAGE_SERVICES = new Set(['servicenow']);
const DEPLOYMENT_STAGE_SERVICES = new Set(['gitops', 'awx']);

export function approvalStallTooltip(
  stallReason?: string | null,
  stallService?: string | null,
): string | null {
  if (!stallService || !APPROVAL_STAGE_SERVICES.has(stallService)) return null;
  return adminActionTooltip(stallReason, stallService);
}

export function deploymentStallTooltip(
  stallReason?: string | null,
  stallService?: string | null,
): string | null {
  if (!stallService || !DEPLOYMENT_STAGE_SERVICES.has(stallService))
    return null;
  return adminActionTooltip(stallReason, stallService);
}
